// Models
import { IExerciseData } from 'storage/exercise/models'
import { TMenuDropdownOption } from 'heeds-ds/src/models'
import { TSpecialSetInput } from 'models'

// React
import { FC, useCallback, useContext, useEffect, useMemo } from 'react'

// Libraries
import { ThemeContext } from 'styled-components'
import { useDispatch } from 'react-redux'
import { useFormContext } from 'react-hook-form'

// Misc
import { buttonClickTracking } from 'utils/tracking'
import { triggerToastError } from 'storage/general/duck'
import useMediaQuery from 'hooks/useMediaQuery'

// Components
import * as Styled from './styled'
import { Aligner, DropdownMenu, Icon, InputText } from 'heeds-ds'
import { ExerciseForm } from 'components'

type Props = {
  onCancel: () => void
  onClickImage: (exercise: IExerciseData) => void
  onRemove: () => void
  openExercisesModal: () => void
  removeExerciseFromWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    exerciseIndex: number,
  ) => void
  specialSet?: TSpecialSetInput
}

const WorkoutSpecialSet: FC<Props> = ({
  onCancel,
  onClickImage,
  onRemove,
  openExercisesModal,
  removeExerciseFromWorkoutSet,
  specialSet,
}) => {
  const {
    unregister,
    formState: { errors, isValid },
  } = useFormContext()
  const theme = useContext(ThemeContext)
  const dispatch = useDispatch()
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.desktop}px)`)

  const workoutSets = useMemo(
    () => specialSet && Object.values(specialSet.exercises),
    [specialSet],
  )

  const options: TMenuDropdownOption[] = [
    {
      color: theme.colors.action.criticalDefault,
      icon: 'delete',
      label: 'Excluir série especial',
      onClick: () => onRemove(),
    },
  ]

  const showToast = useCallback(() => {
    if (!isValid) {
      dispatch(
        triggerToastError({
          customTitle: 'Poucos exercícios adicionados',
          message: 'Séries especiais devem ter pelo menos dois exercícios.',
        }),
      )
    }
  }, [dispatch, isValid])

  useEffect(() => {
    if (Object.keys(errors).length) showToast()
  }, [errors, showToast])

  return (
    <Styled.Container data-testid="workout-special-set-container">
      <Aligner flex="column">
        <Styled.Header>
          <Styled.Title>Serie Especial</Styled.Title>

          {isDesktop && (
            <DropdownMenu items={options}>
              <Styled.IconWrapper>
                <Icon
                  size={24}
                  color={theme.colors.text.default}
                  iconName="moreVertical"
                />
              </Styled.IconWrapper>
            </DropdownMenu>
          )}
        </Styled.Header>

        <Styled.ContentInputs>
          <Styled.Input
            label="Série"
            name="quantity"
            placeholder="0"
            mask="integer"
          />

          <Styled.Input
            label="Intervalo"
            mask="seconds"
            name="interval"
            placeholder="0 seg"
          />
        </Styled.ContentInputs>

        <Styled.ExercisesContainer>
          {workoutSets?.map((exercise, index) => {
            const keyAndName = `exercises.${exercise.id}`
            return (
              <ExerciseForm
                key={keyAndName}
                editMode
                exercise={exercise.exercise}
                name={keyAndName}
                onClickImage={onClickImage}
                onRemove={() => {
                  removeExerciseFromWorkoutSet('', '', index)
                  unregister(keyAndName)
                }}
              />
            )
          })}
        </Styled.ExercisesContainer>

        <Styled.EmptyExercise data-testid="workout-special-set-empty-exercise">
          {isDesktop ? (
            <Styled.Legend>
              {workoutSets?.length ? 'Próximo' : 'Primeiro'} exercício escolhido
              será mostrado aqui
            </Styled.Legend>
          ) : (
            <Styled.MobileExerciseButton
              onClick={openExercisesModal}
              track={buttonClickTracking}
              trackName="open_mobile_exercises_modal"
            >
              Escolher exercício
            </Styled.MobileExerciseButton>
          )}
        </Styled.EmptyExercise>

        <Aligner padding="0 8px">
          <InputText
            label="Comentários sobre a série especial"
            name="comments"
            placeholder="Digite aqui o comentário para o seu aluno"
            margin="0"
            scale="small"
          />
        </Aligner>

        {isDesktop && (
          <Aligner justify="space-between" padding="32px 0">
            <Styled.CancelButton
              onClick={onCancel}
              track={buttonClickTracking}
              trackName="hide_special_workout_set_form"
            >
              Cancelar
            </Styled.CancelButton>

            <Styled.SaveButton
              track={buttonClickTracking}
              trackName="add_new_special_workout_set"
            >
              Salvar série especial
            </Styled.SaveButton>
          </Aligner>
        )}
      </Aligner>
    </Styled.Container>
  )
}

export default WorkoutSpecialSet
